/* eslint-disable complexity */
import {
  Color,
  Combobox,
  DropdownButton,
  NoOptionsPlaceholder,
  Paragraph,
  Tooltip
} from '@eggplant/pattern-library'
import { FiPlus } from 'react-icons/fi'
import { GetAiEnvironmentsRequest } from '@eggplant/api-requests'
import {
  HiExclamation,
  HiOutlineServer
} from 'react-icons/hi'
import {
  LinkToAddEnvironment,
  useSpaceAccess
} from '@eggplant/routing'
import {
  useEffect,
  useState
} from 'react'
import type {
  ButtonShape,
  DropdownPlacement,
  EnvironmentResponse,
  Options
} from '@eggplant/types'

interface Props {
  onChange: (value: string, environmentName: string) => void
  placeholder?: string
  value?: string
  dropdownPlacement?: DropdownPlacement
  isFilterFixedMinMaxWidth?: boolean
  isDisabled?: boolean
  isFilter?: boolean
  hasIcon?: boolean
  executionEnvironmentName?: string
}

const getOptions = (items: EnvironmentResponse[] | undefined) => {
  if (!Array.isArray(items)) return []

  return items.map(item => ({
    label: item.name,
    value: '' + item.id
  }))
}

const getButtonPlaceholderText = (executionEnvironmentName?: string) => {
  if (executionEnvironmentName) {
    return (
      <Paragraph>
        <strong>
          {executionEnvironmentName}
        </strong>
      </Paragraph>
    )
  }

  return (
    <Paragraph>
      <Color variant='theme-placeholder-text'>
        Environment not found
      </Color>
    </Paragraph>
  )
}

const getMissingEeTooltipLabel = (executionEnvironmentName?: string) => {
  if (executionEnvironmentName) {
    return (
      <Paragraph>
        <Color variant='theme-color-tooltip'>
          This environment is not assigned to this space.
          <br/>
          Assign it in the environment page to make it available.
        </Color>
      </Paragraph>
    )
  }

  return (
    <Paragraph>
      <Color variant='theme-color-tooltip'>
        Environment does not exist, please select a new one or remove it.
      </Color>
    </Paragraph>
  )
}

const SelectExecutionEnvironment = ({
  onChange,
  placeholder = 'Execution environment',
  value,
  dropdownPlacement = 'bottom',
  isFilterFixedMinMaxWidth = true,
  isDisabled = false,
  isFilter = true,
  hasIcon = true,
  executionEnvironmentName
}: Props) => {
  const [isAccessible] = useSpaceAccess(['dai:admin'])
  const [isMissingValue, setIsMissingValue] = useState(false)
  const [environmentsData, setEnvironmentsData] = useState<EnvironmentResponse[]>([])

  const handleChange = (newValue: string, data: EnvironmentResponse[]) => {
    const environmentName = data.filter(item => item.id.toString() === newValue)[0]?.name || ''

    onChange(newValue, environmentName)
  }

  const handleSuccess = (data: EnvironmentResponse[]) => {
    setEnvironmentsData(data)
  }

  useEffect(() => {
    if (environmentsData.length === 0) return

    const missingValue = !environmentsData.some(item => item.id.toString() === value)

    if (value && missingValue) {
      setIsMissingValue(true)

      if (!executionEnvironmentName) {
        onChange('', '')
      }

      return
    }

    if (value && !missingValue) {
      setIsMissingValue(false)
    }
  }, [value, executionEnvironmentName, environmentsData, onChange])

  const renderButton = (options: Options) => {
    const currentOption = options.find(item => item.value === value)

    const label = currentOption?.value
      ? currentOption.label
      : placeholder

    const tooltip = currentOption?.value && isFilter
      ? `Execution environment: ${currentOption.label}`
      : label

    const buttonText = currentOption?.value
      ? currentOption.label
      : placeholder

    const buttonPlaceholderText = isMissingValue
      ? getButtonPlaceholderText(executionEnvironmentName)
      : buttonText

    const missingEeTooltipLabel = isMissingValue
      ? getMissingEeTooltipLabel(executionEnvironmentName)
      : undefined

    const filterIcon = hasIcon
      ? HiOutlineServer
      : undefined

    const buttonIcon = isMissingValue
      ? HiExclamation
      : filterIcon

    return (buttonProps: ButtonShape) => {
      const button =
        <DropdownButton
          {...buttonProps}
          Icon={buttonIcon}
          dropdownPlacement={dropdownPlacement}
          isDisabled={isDisabled}
          isFilter={isFilter}
          isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
          isHighlighted={!!value}
          isWarningState={isMissingValue}
          label={label}
          title={tooltip}
        >
          {buttonPlaceholderText}
        </DropdownButton>

      if (missingEeTooltipLabel) {
        return (
          <Tooltip label={missingEeTooltipLabel}>
            {button}
          </Tooltip>
        )
      }

      return button
    }
  }

  const noDataPlaceholder =
    <NoOptionsPlaceholder
      icon={<HiOutlineServer size='24px' />}
      linkTo={
        isAccessible
          ? (
            <LinkToAddEnvironment>
              <FiPlus
                aria-hidden
                size={16}
              />
              New Environment
            </LinkToAddEnvironment>
          )
          : undefined
      }
      title='No execution environment found'
    />

  return (
    <GetAiEnvironmentsRequest
      onSuccess={handleSuccess}
      shortErrorMessage='Failed to load Execution environment'
    >
      {
        (data: EnvironmentResponse[]) =>
          <Combobox
            dropdownPlacement={dropdownPlacement}
            isDisabled={isDisabled}
            isFilter={isFilter}
            isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
            noOptionsPlaceholder={noDataPlaceholder}
            onChange={value => handleChange(value, data)}
            options={getOptions(data)}
            renderButton={renderButton(getOptions(data))}
            value={value}
          />
      }
    </GetAiEnvironmentsRequest>
  )
}

export default SelectExecutionEnvironment
